import React from 'react';
import { useHistory } from 'react-router-dom';
import { RankedTable, StoreName, StoreTypeLegend, YoYBadge } from '../../components';
import type { Column } from '../../components';
import type { Period, StoreRow } from '../../api/types';
import { periodGroups } from '../periodColumns';
import { formatMoney, moneyLabel } from '../../utils/format';

interface Props {
  title: string;
  rows: StoreRow[] | null | undefined;
  viewAllPath: string;
  loading?: boolean;
  period?: Period;
  /** When set, the compare period is shown next to `period` (Sales + YoY for each). */
  comparePeriod?: Period | null;
}

const StoreRankTable: React.FC<Props> = ({ title, rows, viewAllPath, loading, period, comparePeriod }) => {
  const history = useHistory();
  const columns: Column<StoreRow>[] = [
    { key: 'rank', header: '#', width: 22, render: (_r, i) => i + 1 },
    {
      key: 'name',
      header: 'Store Name',
      render: (r) => <StoreName name={r.store_name} type={r.store_type} />,
    },
    { key: 'sales', header: `Sales (${moneyLabel()})`, align: 'right', width: 68, render: (r) => formatMoney(r.sales) },
    {
      key: 'yoy',
      header: 'YoY %',
      align: 'right',
      width: 80,
      render: (r) => <YoYBadge value={r.yoy_pct} isNew={r.store_type === 'NEW_STORE' && r.yoy_pct == null} />,
    },
  ];
  const dual = period && comparePeriod
    ? periodGroups<StoreRow>(
        period, comparePeriod,
        { sales: (r) => r.sales, yoy: (r) => r.yoy_pct },
        { sales: (r) => r.compare_sales, yoy: (r) => r.compare_yoy_pct },
        56, 48, (v) => <YoYBadge value={v ?? null} variant="inline" showArrow={false} />,
      )
    : null;
  const tableColumns = dual ? [columns[0], columns[1], ...dual.columns] : columns;
  return (
    <RankedTable
      title={title}
      columns={tableColumns}
      headerGroups={dual?.groups}
      legend={<StoreTypeLegend />}
      rows={rows}
      rowKey={(r) => r.site_code}
      loading={loading}
      onRowClick={(r) => history.push(`/app/store/${r.site_code}`)}
      className={dual ? 'su-rank-dual' : undefined}
      onViewAll={() => history.push(viewAllPath)}
      viewAllLabel="View All >"
    />
  );
};

export default StoreRankTable;
